"use client";

import LogOut from "@components/LogOut";
import { FileWithPath } from "@interfaces/file";
import dynamic from "next/dynamic";
import {FormEvent, useEffect, useState} from "react";
import { useUpload } from "../_context/UploadContext";
import useAPI from "../_hooks/useAPI";

const GenerateButton = dynamic(() => import("@components/buttons/Sparkle"));
const Details = dynamic(() => import("@components/atoms/Details"), {
  ssr: false,
});
const Form = dynamic(() => import("@components/upload/Form"), { ssr: false });
const Notification = dynamic(() => import("@components/atoms/Notification"));
const UploadHeader = dynamic(() => import("@components/upload/Header"), {
  ssr: false,
});
const XlsxUpload = dynamic(() => import("@components/upload/XlsxUpload"));

const UploadXlsx = () => {
  const { uploading, complete, files, notification, setNotification } =
    useUpload();
  const { uploadFiles, removeFile, changeFile } = useAPI();
  const [progress, setProgress] = useState(0);

  // state to manage the selected account and projectIDs
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedPractice, setSelectedPractice] = useState<string>("");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");

  const filesWithPath = files.map(
    (file) =>
      ({
        ...file,
        path: file.name,
        size: file.size || 0,
      }) as FileWithPath,
  );

  useEffect(() => {
    if (notification && !uploading) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [notification, setNotification, uploading]);

  useEffect(() => {
    if (uploading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      return () => {
        clearInterval(interval);
        setProgress(100);
      };
    }
  }, [uploading]);

  const hasFiles =
    files.length >= 1 &&
    files[0].type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const hasMultipleFiles = files.length > 1;

  const isGeneratedDisabled = uploading ||
      !selectedAccount ||
      !selectedProject ||
      !selectedPractice ||
      !selectedQuarter ||
      files.length === 0;

  const handleUploadSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isGeneratedDisabled) {
      setNotification({
        status: "error",
        message: "Please fill all fields and select a file."
      });
      return;
    }
    uploadFiles({
      accountID: selectedAccount,
      projectID: selectedProject,
      practice: selectedPractice,
      quarter: selectedQuarter,
    });
  }


  return (
    <div className="w-full h-dvh flex flex-col justify-center items-center pt-40 pb-16 px-2 md:px-0 overflow-scroll no-scrollbar">
      <LogOut />
      <UploadHeader />
      <main className="max-w-7xl py-16 flex flex-col justify-center items-center select-none gap-4 font-body">
        {hasFiles && (
          <XlsxUpload
            files={filesWithPath}
            uploading={uploading}
            remove={removeFile}
          />
        )}
        {!uploading && !complete && (
          <Form
              // state handlers
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            selectedPractice={selectedPractice}
            setSelectedPractice={setSelectedPractice}
            selectedQuarter={selectedQuarter}
            setSelectedQuarter={setSelectedQuarter}
            handleUpload={handleUploadSubmit}
            files={files}
            handleFileChange={changeFile}
            acceptedFileTypes=".xlsx"
          />
        )}
        {notification && (
          <Notification
            status={notification.status}
            message={notification.message}
            progress={uploading ? progress : undefined}
          />
        )}
        {!uploading && files.length > 0 && !hasMultipleFiles && (
          <GenerateButton disabled={uploading} complete={complete} />
        )}
      </main>
      <Details open={files.length === 0} />
    </div>
  );
};

export default UploadXlsx;
